import type { Value } from '@skop-app/lib-domain/types'

import type {
  ExternalFilters,
  ExternalFiltersConfig,
  GridScope,
} from '../types'
import {
  buildScopeKey,
  buildStorageKey,
  readStorageItem,
  removeStorageItem,
  writeStorageItem,
} from '../storage'

const isValue = (value: unknown): value is Value =>
  typeof value === 'string' || typeof value === 'number'

const isValueArray = (value: unknown): value is Value[] =>
  Array.isArray(value) && value.every(isValue)

const isExternalFilters = (value: unknown): value is ExternalFilters => {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return Object.values(record).every(isValueArray)
}

export const parseExternalFiltersString = (
  raw: string,
): ExternalFilters | null => {
  try {
    const parsed: unknown = JSON.parse(raw)
    return isExternalFilters(parsed) ? parsed : null
  } catch {
    return null
  }
}

export const parseExternalFilters = (raw: string | null): ExternalFilters => {
  if (!raw) return {}
  return parseExternalFiltersString(raw) ?? {}
}

export const areExternalFiltersEqual = (
  a: ExternalFilters,
  b: ExternalFilters,
): boolean => {
  const aKeys = Object.keys(a).sort()
  const bKeys = Object.keys(b).sort()
  if (aKeys.length !== bKeys.length) return false
  for (let i = 0; i < aKeys.length; i++) {
    const key = aKeys[i]
    if (key !== bKeys[i]) return false
    const aVals = a[key] ?? []
    const bVals = b[key] ?? []
    if (aVals.length !== bVals.length) return false
    for (let j = 0; j < aVals.length; j++) {
      if (aVals[j] !== bVals[j]) return false
    }
  }
  return true
}

export const pruneExternalFilters = (
  filters: ExternalFilters,
  allowlist: Readonly<Record<string, unknown>>,
): ExternalFilters => {
  const next: ExternalFilters = {}
  Object.keys(allowlist).forEach((field) => {
    const values = filters[field]
    if (!Array.isArray(values) || values.length === 0) return
    // Value[] is already narrow, but stay defensive when data comes from storage.
    const safe = values.filter(isValue)
    if (safe.length > 0) next[field] = safe
  })
  return next
}

export const getExternalFiltersStorageKey = <Row, Ctx>(
  config: ExternalFiltersConfig<Row, Ctx>,
  scope: GridScope,
): string => {
  if (config.storageKey) return config.storageKey(scope)
  const scopeKey = buildScopeKey(scope)
  return buildStorageKey('grid-filters', scopeKey)
}

export const getExternalFiltersLegacyKey = <Row, Ctx>(
  config: ExternalFiltersConfig<Row, Ctx>,
  scope: GridScope,
): string => {
  return config.legacyStorageKey ? config.legacyStorageKey(scope) : ''
}

type LoadSource = 'current' | 'legacy' | 'default' | 'empty'

export type ExternalFiltersLoadResult = {
  selected: ExternalFilters
  storageKey: string
  legacyKey: string
  source: LoadSource
}

export const loadExternalFiltersFromStorage = <Row, Ctx>(params: {
  config: ExternalFiltersConfig<Row, Ctx>
  scope: GridScope
}): ExternalFiltersLoadResult => {
  const { config, scope } = params
  const persist = config.persist !== false
  const migrateLegacy = config.migrateLegacy !== false
  const storageKey = getExternalFiltersStorageKey(config, scope)
  const legacyKey = getExternalFiltersLegacyKey(config, scope)

  const allowlist = config.fields

  const resolveDefault = (): ExternalFilters => {
    const defaults = config.defaultSelected ?? {}
    return pruneExternalFilters(defaults, allowlist)
  }

  if (!persist) {
    const defaults = resolveDefault()
    return {
      selected: defaults,
      storageKey,
      legacyKey,
      source: Object.keys(defaults).length > 0 ? 'default' : 'empty',
    }
  }

  const saveOrRemove = (selected: ExternalFilters) => {
    if (Object.keys(selected).length === 0) {
      removeStorageItem(storageKey)
      return
    }
    writeStorageItem(storageKey, JSON.stringify(selected))
  }

  const readAndPrune = (raw: string | null): ExternalFilters | null => {
    if (!raw) return null
    const parsed = parseExternalFiltersString(raw)
    if (!parsed) return null
    return pruneExternalFilters(parsed, allowlist)
  }

  const currentRaw = readStorageItem(storageKey)
  if (currentRaw) {
    const parsed = parseExternalFiltersString(currentRaw)
    if (!parsed) {
      removeStorageItem(storageKey)
    } else {
      const pruned = pruneExternalFilters(parsed, allowlist)
      if (!areExternalFiltersEqual(pruned, parsed)) {
        saveOrRemove(pruned)
      }
      return { selected: pruned, storageKey, legacyKey, source: 'current' }
    }
  }

  if (migrateLegacy && legacyKey) {
    const legacyRaw = readStorageItem(legacyKey)
    const legacyPruned = readAndPrune(legacyRaw)
    if (legacyPruned && Object.keys(legacyPruned).length > 0) {
      saveOrRemove(legacyPruned)
      return { selected: legacyPruned, storageKey, legacyKey, source: 'legacy' }
    }
  }

  const defaults = resolveDefault()
  if (Object.keys(defaults).length > 0) {
    // Keep behavior consistent with the hook: defaults become the persisted baseline.
    saveOrRemove(defaults)
    return { selected: defaults, storageKey, legacyKey, source: 'default' }
  }

  removeStorageItem(storageKey)
  return { selected: {}, storageKey, legacyKey, source: 'empty' }
}
